import React, { useEffect, useState } from 'react';
import { Message } from '@paypalcorp/worldready-react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _isString from 'lodash/isString';
import { chatBotMessageUsRefIdsMapping } from '../../../js/common/commonConstants';
import poll from '../../../js/lib/poll';
import Mutation from '../../../js/lib/mutation-observer';
import { generateLinkData, trackActivityEvent, trackClickEvent } from '../../helpers/analytics/fptiHelper';
import {
  handleChatBotPromptOrBtnShownFPTIEvent,
  handleChatbotWindowOpen,
  hideChatBot,
  isChatCardClicked,
} from '../../helpers/chatBotHelper';
import { setChatCardClickInSession } from '../../../js/lib/chat-bot-live-helper';
import { FPTI_DATA } from '../../common/propTypes';
import { isContactUsPage } from '../../../js/lib/globalHelper';
import { isLoggedIn } from '../../helpers/twHelper';

const validateFpti = () => {
  return new Promise((resolve, reject) => {
    if (!window.fpti) {
      reject(false);
    } else {
      resolve(window.fpti);
    }
  });
};

const Chatbot = function (props) {
  const {
    link,
    hpid,
    isChatBotAsyncUser,
    chatPillOcpParams,
    chatBotClicked,
    topicRefId,
    topicName: subject,
    display,
    FPTIData,
  } = props;
  const [chatBotPromptClose, setChatBotPromptClose] = useState(false);

  const wasChatCardClicked = isChatCardClicked();

  const handleChatBotPromptClasses = () =>
    classNames({
      'sh_chat_bot_chat_question_card sh_chat_bot_fade_in': !chatBotPromptClose,
      'sh_chat_bot_chat_question_card_close sh_chat_bot_fade_out': chatBotPromptClose,
    });

  const handleChatNowBtnClasses = () => {
    const classes = new Set(['sh_chat_bot_chat_btn']);
    if (!wasChatCardClicked) classes.add('sh_chat_bot_chat_btn_hidden');
    if (wasChatCardClicked || chatBotPromptClose) classes.add('sh_chat_bot_btn_fadein sh_chat_bot_chat_btn_show');

    return Array.from(classes).join(' ');
  };

  const chatBotTracking = () => {
    if (isContactUsPage() && _isString(topicRefId) && topicRefId !== chatBotMessageUsRefIdsMapping.GENPOOL_CONTACT_US)
      return Promise.resolve();

    return poll(validateFpti, 4, 500);
  };

  useEffect(() => {
    if (display) {
      Mutation.waitForBodyToLoad(Mutation.nodeChatObserver);
      chatBotTracking();

      handleChatBotPromptOrBtnShownFPTIEvent({ finalIntentRefId: topicRefId });
    }
  }, [display]);

  // fire fpti event for chatbot message button shown only when Chat card clicked
  useEffect(() => {
    if (chatBotPromptClose) {
      const fptiData = {
        ...FPTIData.chatBotMessageBtnContactUsShown,
        link: generateLinkData(FPTIData.chatBotMessageBtnContactUsShown.link, { topicRefId }),
      };
      trackActivityEvent(fptiData);
    }
  }, [chatBotPromptClose]);

  const handleClose = async (e) => {
    e.preventDefault();
    e.cancelBubble = true; // otherwise, close will bubble up and invoke chat bot.
    if (e.stopPropagation) e.stopPropagation();

    setChatBotPromptClose(true);
    setChatCardClickInSession(true);

    // fpti event for chatbot prompt close
    const fptiData = {
      ...FPTIData.chatBotPromptContactUsClose,
      link: generateLinkData(FPTIData.chatBotPromptContactUsClose.link, { topicRefId }),
    };
    trackClickEvent(fptiData);
  };

  const getPromptMessage = () => {
    if (isChatBotAsyncUser) {
      return <Message id="smarthelp.chatBot.asyncPrompt.question" />;
    }
    return <Message id="smarthelp.chatBot.message.question" />;
  };

  const getBubbleText = () => {
    if (isChatBotAsyncUser) {
      return <Message id="smarthelp.chatBot.asyncBubble.text" />;
    }
    return <Message id="smarthelp.chatBot.button.text" />;
  };

  const handleClick = () => {
    // Track chat bot click
    const fptiData = {
      ...FPTIData.chatBotContactUsClick,
      link: generateLinkData(FPTIData.chatBotContactUsClick.link, { topicRefId }),
    };
    trackClickEvent(fptiData);
    // Logged Out Logic
    if (!isLoggedIn()) return chatBotClicked(isChatBotAsyncUser);
    return handleChatbotWindowOpen({ topicRefId, subject, link, hpid, isChatBotAsyncUser, chatPillOcpParams });
  };

  const chatBotPromptNotClickedBefore = () => {
    return (
      <div
        id="chat-question-card"
        className={handleChatBotPromptClasses()}
        data-testid="sh-chat-bot-chat-question-card"
      >
        <button
          onClick={handleClose}
          className="sh_chat_bot_close_btn"
          aria-label="close"
          data-testid="sh-chat-bot-close-btn"
        />
        <p className="sh_chat_bot_ppl_logo" data-testid="sh-chat-bot-ppl-logo" />
        <p className="sh_chat_bot_chatbot_question" data-testid="sh-chat-bot-chatbot-question">
          {getPromptMessage()}
        </p>
        <p className="sh_chat_bot_chatbot_text" data-testid="sh-chat-bot-chatbot-text">
          <Message id="smarthelp.chatBot.bubble.text" />
        </p>
        <button
          onClick={() => handleClick()}
          className="sh_chat_bot_chat_question_card_btn"
          data-testid="sh-chat-bot-chat-question-card-btn"
        >
          <span className="sh_chat_bot_chat_flex" data-testid="sh-chat-bot-chat-flex">
            <span className="sh_chat_bot_chat_icon" aria-hidden="true" data-testid="sh-chat-bot-chat-icon" />
            <p className="sh_chat_bot_btn_text" data-testid="sh-chat-bot-btn-text">
              {getBubbleText()}
            </p>
          </span>
        </button>
        <button onClick={handleClose} className="sh_chat_bot_reject_text" data-testid="sh-chat-bot-reject-text">
          <Message id="smarthelp.chatBot.bubble.reject" />
        </button>
      </div>
    );
  };

  const chatBotPromptClickedOnce = () => {
    return (
      <button
        id="chat-now-btn"
        onClick={() => handleClick()}
        className={handleChatNowBtnClasses()}
        data-testid="sh-chat-bot-chat-btn"
      >
        <span className="sh_chat_bot_chat_flex" data-testid="sh-chat-bot-chat-flex">
          <span className="sh_chat_bot_chat_icon" aria-hidden="true" data-testid="sh-chat-bot-chat-icon" />
          <p className="sh_chat_bot_btn_text" data-testid="sh-chat-bot-btn-text">
            {getBubbleText()}
          </p>
        </span>
      </button>
    );
  };

  if (hideChatBot(display)) return null;

  return (
    <div id="smarthelp-chatBot-prompt" className="sh_chat_bot_prompt" data-testid="sh-chat-bot-prompt">
      {!wasChatCardClicked && chatBotPromptNotClickedBefore()}
      {chatBotPromptClickedOnce()}
    </div>
  );
};

Chatbot.propTypes = {
  link: PropTypes.string.isRequired,
  hpid: PropTypes.string.isRequired,
  isChatBotAsyncUser: PropTypes.bool.isRequired,
  chatBotClicked: PropTypes.func,
  chatPillOcpParams: PropTypes.string,
  topicRefId: PropTypes.string,
  topicName: PropTypes.string,
  display: PropTypes.bool.isRequired,
  FPTIData: PropTypes.shape(FPTI_DATA),
};

Chatbot.defaultProps = {
  id: '',
  link: '',
  hpid: '',
  display: false,
  topicRefId: 'GENPOOL', // GENPOOL is generic topic id
  chatBotClicked: () => {},
};

export default Chatbot;
